import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	forBusiness_realEstate_enhancement,
	forBusiness_realEstate_faster,
	forBusiness_realEstate_features,
	forBusiness_realEstate_listing,
	forBusiness_realEstate_matter,
	forBusiness_realEstate_perfect,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import enhancement from '@/public/images/for-business/realEstate/enhancement.webp';
import faster from '@/public/images/for-business/realEstate/faster.webp';
import features from '@/public/images/for-business/realEstate/features.webp';
import imgHero from '@/public/images/for-business/realEstate/hero.webp';
import listings from '@/public/images/for-business/realEstate/listings.webp';
import matter from '@/public/images/for-business/realEstate/matter.webp';
import perfect from '@/public/images/for-business/realEstate/perfect.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './RealEstateLanding.module.scss';

interface Props {}
const RealEstateLanding: React.FC<Props> = () => {
	const { t } = useTranslationWithLocale('real-estate-listings', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.REAL_ESTATE_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.REAL_ESTATE_LANDING },
	);

	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.REAL_ESTATE_LANDING });
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['estate__hero-title'],
					picture: styles['estate__hero-subtitle'],
				}}
			/>

			{/* faster */}
			<ImageTextSection
				title={t('fasterData_mainTitle')}
				subtitle1={t('fasterData_mainSubtitle1')}
				image={faster}
				specialListItem
				data={forBusiness_realEstate_faster}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* matter */}
			<ImageTextSection
				title={t('matterData_mainTitle')}
				image={matter}
				data={forBusiness_realEstate_matter}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['estate__matter-image-container'],
					list: styles['estate__matter-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* enhancement */}
			<ImageTextSection
				title={t('enhancementData_mainTitle')}
				image={enhancement}
				data={forBusiness_realEstate_enhancement}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* listings */}
			<ImageTextSection
				title={t('listingsData_mainTitle')}
				image={listings}
				data={forBusiness_realEstate_listing}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['estate__listings-image-container'],
					list: styles['estate__listings-list'],
					section: styles['estate__listings-section'],
				}}
			/>

			{/* features */}
			<ImageTextSection
				title={t('featuresData_mainTitle')}
				image={features}
				data={forBusiness_realEstate_features}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['estate__features-section'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_realEstate_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['estate__perfect-image-container'],
					list: styles['estate__perfect-list'],
				}}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['estate__tryLift-title'],
					subtitleClass: styles['estate__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default RealEstateLanding;
